import React, { useState, forwardRef, useRef, createRef } from 'react';
import _merge from 'lodash/merge';
import Icon from 'components/Icon';
import { useCombinedRefs, getContext, KEY_CODES } from 'common';
import useHover from 'common/useHover';

import CheckboxRipple from './CheckboxRipple';
import CheckboxLabelContent from './CheckboxLabelContent';
import CheckboxErrorMessage from './CheckboxErrorMessage';

import Styled from './styles';

import CHECKED_SVG from './icons/ic_checkbox_checked';
import UNCHECKED_SVG from './icons/ic_checkbox_unchecked';
import INDETERMINATE_SVG from './icons/ic_checkbox_indeterminate';

const COMMON_ICON_STYLE = {
  RTL: false,
  style: {
    position: 'relative' as const,
    top: '0',
    left: '0',
  },
  size: {
    width: '1.25rem',
    height: '1.25rem'
  },
};

export type CheckBoxIconProps = {
  hovered?: boolean;
  disabled?: boolean;
  theme?: any;
};

const CHECKED_ICON_CONFIG = (disabled, theme) => ({
  color: disabled ? theme.checked.disabled.icon : theme.checked.icon,
  ...COMMON_ICON_STYLE
});

const UNCHECKED_ICON_CONFIG = (disabled, hovered, theme) => ({
  color: disabled ? theme.unchecked.disabled.icon : hovered ? theme.unchecked.hover.icon : theme.unchecked.icon,
  ...COMMON_ICON_STYLE
});

const INDETERMINATE_ICON_CONFIG = (disabled, theme) => ({
  color: disabled ? theme.indeterminate.disabled.icon : theme.indeterminate.icon,
  ...COMMON_ICON_STYLE
});

export interface CheckedIconProps {
  hovered?: boolean;
  disabled?: boolean;
  theme?: Record<string, unknown>;
  iconProps?: any;
}

const CheckedIcon = ({
  disabled,
  theme,
  ...otherProps
}: CheckedIconProps) =>
  <Icon {...CHECKED_ICON_CONFIG(disabled, theme)} {...otherProps}>{CHECKED_SVG}</Icon>;

CheckedIcon.defaultProps = {
  hovered: false,
  disabled: false,
  theme: {},
  iconProps: {},
};
export interface UncheckedIconProps {
  hovered?: boolean;
  disabled?: boolean;
  theme?: Record<string, unknown>;
}

const UncheckedIcon = ({
  disabled,
  hovered,
  theme,
  ...otherProps
}: UncheckedIconProps) =>
  <Icon {...otherProps} {...UNCHECKED_ICON_CONFIG(disabled, hovered, theme)}>{UNCHECKED_SVG}</Icon>;

UncheckedIcon.defaultProps = {
  hovered: false,
  disabled: false,
  theme: {}
};
export interface IndeterminateIconProps {
  hovered?: boolean;
  disabled?: boolean;
  theme?: Record<string, unknown>;
}

const IndeterminateIcon = ({
  disabled,
  theme,
  ...otherProps
}: IndeterminateIconProps) =>
  <Icon {...otherProps} {...INDETERMINATE_ICON_CONFIG(disabled, theme)}>{INDETERMINATE_SVG}</Icon>;

IndeterminateIcon.defaultProps = {
  hovered: false,
  disabled: false,
  theme: {}
};

const INPUT_CONFIG = {
  type: 'checkbox'
};

export interface CheckboxProps extends React.ComponentPropsWithRef<'div'> {
  /** Callback fired when the state is changed. Signature: `function(event: object) => void`, event: The event source of the callback. The new checked state: boolean `event.target.checked`. */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** If `true`, the component is checked. Note: The native input element's `checked` attribute will be set using this prop. */
  checked?: boolean;
  /** If `true`, the component will be rendered with indeterminate visuals. */
  indeterminate?: boolean;
  /** If `true`, the component will be disabled. Note: The native input element's `disabled` attribute will be set using this prop. */
  disabled?: boolean;
  /** If `true`, the component will automatically get focus when the page loads. Note: The native input element's `autofocus` attribute will be set using this prop. */
  autoFocus?: boolean;
  /**  Attributes applied to the input element. (excluding { type: 'checkbox' }). Note: DOM attributes should be camelCased. */
  inputProps?: React.ComponentPropsWithRef<'input'>,
  /** To override the checked state icon, the icon component will receive all Checkbox props and the configured theme. */
  checkedIcon?: (arg: CheckBoxIconProps) => JSX.Element;
  /** To override the indeterminate state icon, the icon component will receive all Checkbox props and the configured theme. */
  indeterminateIcon?: (arg: CheckBoxIconProps) => JSX.Element;
  /** To override the unchecked state icon, the icon component will receive all Checkbox props and the configured theme. */
  uncheckedIcon?: (arg: CheckBoxIconProps) => JSX.Element;
  /** Properties to forward to the Icon component. Useful for screen reader.  */
  iconProps?: any,
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/**
 * Uncontrolled Checkbox.
 * Any other props than the `Checkbox` props will be provided to the root element.
 * To alter the DOM attributes applied to the internal `input` element, please use `inputProps`.
 * To get the ref of the input field element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 * */
const Checkbox = forwardRef<HTMLElement, CheckboxProps>((props: CheckboxProps, ref) => getContext(({ theme }) => {
  const {
    checked,
    disabled,
    indeterminate,
    onChange,
    autoFocus,
    inputProps,
    checkedIcon,
    indeterminateIcon,
    uncheckedIcon,
    iconProps,
    theme: themeProp,
    ...wrapperProps
  } = props;

  const rippleRef = useRef(null);
  const resolvedTheme = _merge({}, theme.CHECKBOX, themeProp);

  const [focused, setIsFocused] = useState(autoFocus);
  const inputEl = createRef<HTMLElement>();
  const combinedRef = useCombinedRefs(ref, inputEl);
  const [hoverRef, hovered] = useHover();

  const resolvedWrapperProps = {
    ...wrapperProps,
    $disabled: disabled
  };

  const runRipple = () => {
    rippleRef.current.runRipple();
  };

  const asyncHideRipple = () => {
    rippleRef.current.asyncHideRipple();
  };

  const mapComponentPropsToNativeAttributes = () => ({
    autoFocus,
    checked,
    disabled,
    indeterminate,
    onChange
  });

  const resolvedInputProps = {
    ...mapComponentPropsToNativeAttributes(),
    ...inputProps,
    ref: combinedRef,
    onKeyDown: (data) => { data.key === KEY_CODES.SPACE_KEY && runRipple(); inputProps.onKeyDown && inputProps.onKeyDown(data); },
    onKeyUp: (data) => { data.key === KEY_CODES.SPACE_KEY && asyncHideRipple(); inputProps.onKeyUp && inputProps.onKeyUp(data); },
    onMouseDown: (data) => { runRipple(); inputProps.onMouseDown && inputProps.onMouseDown(data); },
    onMouseOut: (data) => { asyncHideRipple(); inputProps.onMouseOut && inputProps.onMouseOut(data); },
    onMouseUp: (data) => { asyncHideRipple(); inputProps.onMouseUp && inputProps.onMouseUp(data); },
    onClick: (data) => { inputEl && inputEl.current && inputEl.current.focus(); inputProps.onClick && inputProps.onClick(data); },
    onFocus: (data) => { setIsFocused(true); inputProps.onFocus && inputProps.onFocus(data); },
    onBlur: (data) => { setIsFocused(false); inputProps.onBlur && inputProps.onBlur(data); },
    ...INPUT_CONFIG,
  };

  const getIcon = () => {
    if (checked) {
      return checkedIcon({ disabled, theme: resolvedTheme, hovered: !!hovered, ...iconProps });
    }
    if (indeterminate) {
      return indeterminateIcon({ disabled, theme: resolvedTheme, hovered: !!hovered, ...iconProps });
    }
    return uncheckedIcon({ disabled, theme: resolvedTheme, hovered: !!hovered, ...iconProps });
  };

  return (
    <Styled.Wrapper {...resolvedWrapperProps} ref={hoverRef}>
      {getIcon()}
      {focused && <Styled.FocusBorder $theme={resolvedTheme} />}
      {hovered && <Styled.HoveredStyle checked={checked} $theme={resolvedTheme} />}
      <CheckboxRipple ref={rippleRef} $theme={resolvedTheme} />
      <Styled.Input {...resolvedInputProps} />
    </Styled.Wrapper>
  );
}));

Checkbox.defaultProps = {
  onChange: undefined,
  checked: false,
  indeterminate: false,
  disabled: false,
  autoFocus: false,
  inputProps: {},
  checkedIcon: CheckedIcon,
  indeterminateIcon: IndeterminateIcon,
  uncheckedIcon: UncheckedIcon,
  iconProps: {},
  theme: {}
};

Checkbox.displayName = 'Checkbox';

export {
  Checkbox as default,
  CheckboxLabelContent,
  CheckboxErrorMessage,
};
